import { DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzTableModule, type NzTableSortOrder } from 'ng-zorro-antd/table';
import { BATTERY_SIZING_STATUS } from '../../constants/battery-sizing.constants';
import {
  BatterySizingConfiguration,
  BatterySizingRangeResult,
} from '../../models';
import { AppMessageService } from '../../services';

type GroupedRangeView = {
  rangeCode: string;
  rangeName?: string | null;
  items: Array<BatterySizingRangeResult & { _key: string }>;
  hasSelected: boolean;
};

type SortableColumn = 'blockCount' | 'capacity' | 'backupTime' | 'price';

@Component({
  selector: 'app-results-table',
  imports: [
    NzTableModule,
    NzRadioModule,
    FormsModule,
    NzAlertModule,
    DecimalPipe,
    NzButtonComponent,
    NzIconDirective,
  ],
  templateUrl: './results-table.component.html',
  styleUrl: './results-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResultsTableComponent {
  private readonly defaultVisibleItemsPerRange = 4;
  private readonly appMessageService = inject(AppMessageService);
  ranges = input<BatterySizingRangeResult[]>([]);
  loading = input<boolean>(false);
  selectionEnabled = input<boolean>(true);
  protected readonly statuses = BATTERY_SIZING_STATUS;

  protected resolveInfeasibleMessage(reason?: string | null): string {
    const fallback = this.appMessageService.getMessage(
      'noFeasibleConfigurationWithinParallelLimit',
      'No feasible configuration within parallel limit',
    );

    if (!reason) {
      return fallback;
    }

    if (
      reason === 'No feasible configuration within parallel limit' ||
      reason === 'No feasible configuration available for this range.'
    ) {
      return fallback;
    }

    return reason;
  }

  // Output selection change to parent
  selectedChange = output<BatterySizingRangeResult>();

  // Group ranges by rangeCode and order groups so the selected group appears first.
  // Inside the selected group, ensure the selected result is the first row.
  readonly groupedRanges = computed<GroupedRangeView[]>(() => {
    const items = this.ranges() ?? [];
    const map = new Map<
      string,
      {
        rangeCode: string;
        rangeName?: string | null;
        items: BatterySizingRangeResult[];
        hasSelected: boolean;
      }
    >();

    for (const item of items) {
      const key = item.rangeCode;
      const existing = map.get(key);
      if (existing) {
        existing.items.push(item);

        if (item.status === BATTERY_SIZING_STATUS.Selected) {
          existing.hasSelected = true;
        }

        if (existing.rangeName == null && item.rangeName != null) {
          existing.rangeName = item.rangeName;
        }
      } else {
        map.set(key, {
          rangeCode: item.rangeCode,
          rangeName: item.rangeName ?? null,
          items: [item],
          hasSelected: item.status === BATTERY_SIZING_STATUS.Selected,
        });
      }
    }

    const groups = Array.from(map.values());

    // Sort groups so that the one containing a selected result is first.
    groups.sort(
      (resultA, resultB) =>
        Number(resultB.hasSelected) - Number(resultA.hasSelected),
    );

    groups.forEach((group) => {
      group.items = this.sortGroupItems(group.items);
    });

    // Attach stable keys that survive local sorting.
    const view: GroupedRangeView[] = groups.map((group) => ({
      rangeCode: group.rangeCode,
      rangeName: group.rangeName ?? null,
      hasSelected: group.hasSelected,
      items: group.items.map((item, itemIndex) => ({
        ...item,
        _key: `${group.rangeCode}:${item.configuration?.batteryDocumentId ?? 'no-battery'}:${item.configuration?.parallels ?? 'np'}:${item.configuration?.seriesBlocks ?? 'ns'}:${itemIndex}`,
      })),
    }));

    return view;
  });

  // Holds the selected row stable key
  readonly selectedKey = signal<string | null>(null);
  readonly expandedRangeCodes = signal<Set<string>>(new Set());
  readonly activeSort = signal<{
    column: SortableColumn | null;
    order: NzTableSortOrder;
  }>({
    column: 'price',
    order: 'ascend',
  });

  // Auto-preselect the item with status 'selected' when groups change
  readonly syncSelectedFromResults = effect(() => {
    const groups = this.groupedRanges();
    const current = this.selectedKey();
    const canSelect = this.selectionEnabled();

    // Collect selectable keys (ignore infeasible rows)
    const validKeys = new Set<string>(
      groups.flatMap((group) =>
        group.items
          .filter((item) => item.status !== BATTERY_SIZING_STATUS.Infeasible)
          .map((item) => item._key),
      ),
    );

    // Keep current if still valid (respects user choice)
    if (current && validKeys.has(current)) {
      return;
    }

    // Otherwise find first item marked as selected
    const preselect = this.findFirstSelectedKey(groups);

    if (preselect) {
      this.selectedKey.set(preselect);
      return;
    }

    if (canSelect) {
      const firstSelectable = this.findFirstSelectableKey(groups);
      if (firstSelectable) {
        this.onSelectChange(firstSelectable);
        return;
      }
    } else if (current && !validKeys.has(current)) {
      this.selectedKey.set(null);
    }
  });

  readonly syncExpandedRanges = effect(() => {
    const rangeCodes = new Set(
      this.groupedRanges().map((group) => group.rangeCode),
    );
    this.expandedRangeCodes.update((expandedRangeCodes) => {
      const nextExpandedRangeCodes = new Set<string>();
      expandedRangeCodes.forEach((rangeCode) => {
        if (rangeCodes.has(rangeCode)) {
          nextExpandedRangeCodes.add(rangeCode);
        }
      });
      return nextExpandedRangeCodes;
    });
  });

  // Find first selected item key without nested/labeled loops
  protected findFirstSelectedKey(groups: GroupedRangeView[]): string | null {
    const found = groups
      .flatMap((group) => group.items)
      .find((item) => item.status === BATTERY_SIZING_STATUS.Selected);
    return found ? found._key : null;
  }

  protected findFirstSelectableKey(groups: GroupedRangeView[]): string | null {
    const found = groups
      .flatMap((group) => group.items)
      .find((item) => item.status !== BATTERY_SIZING_STATUS.Infeasible);
    return found ? found._key : null;
  }

  protected findByCompositeId(
    id: string | null,
  ): BatterySizingRangeResult | undefined {
    if (!id) {
      return undefined;
    }

    const selectedItem = this.groupedRanges()
      .flatMap((group) => group.items)
      .find((item) => item._key === id);

    return selectedItem as BatterySizingRangeResult | undefined;
  }

  protected onSelectChange = (id: string | null) => {
    this.selectedKey.set(id);
    const found = this.findByCompositeId(id);
    if (found) {
      this.selectedChange.emit(found);
    }
  };

  protected selectItem(id: string): void {
    if (!this.selectionEnabled()) {
      return;
    }

    this.onSelectChange(id);
  }

  protected capacityLabel(config?: BatterySizingConfiguration): string {
    const outputLabel = this.normalizeNumericValue(config?.c10OutputLabelAh);
    if (outputLabel !== null) {
      return String(outputLabel);
    }

    const nominal = this.normalizeNumericValue(config?.c10NominalAh);
    if (nominal !== null) {
      return String(nominal);
    }

    return '-';
  }

  protected visibleItems(
    group: GroupedRangeView,
  ): Array<BatterySizingRangeResult & { _key: string }> {
    if (this.isRangeExpanded(group.rangeCode)) {
      return group.items;
    }

    return group.items.slice(0, this.defaultVisibleItemsPerRange);
  }

  protected hiddenItemsCount(group: GroupedRangeView): number {
    return Math.max(group.items.length - this.defaultVisibleItemsPerRange, 0);
  }

  protected canToggleRange(group: GroupedRangeView): boolean {
    return group.items.length > this.defaultVisibleItemsPerRange;
  }

  protected rangeToggleLabel(group: GroupedRangeView): string {
    if (this.isRangeExpanded(group.rangeCode)) {
      return 'Show less';
    }

    const hiddenItems = this.hiddenItemsCount(group);
    return `Show ${hiddenItems} more result${hiddenItems > 1 ? 's' : ''}`;
  }

  protected rangeToggleIcon(group: GroupedRangeView): string {
    return this.isRangeExpanded(group.rangeCode) ? 'up' : 'down';
  }

  protected sortOrder(column: SortableColumn): NzTableSortOrder {
    const activeSort = this.activeSort();
    if (activeSort.column !== column) {
      return null;
    }

    return activeSort.order;
  }

  protected handleSortOrderChange(
    column: SortableColumn,
    order: NzTableSortOrder,
  ): void {
    this.activeSort.set({
      column: order ? column : null,
      order,
    });
  }

  protected toggleRange(rangeCode: string): void {
    this.expandedRangeCodes.update((expandedRangeCodes) => {
      const nextExpandedRangeCodes = new Set(expandedRangeCodes);
      if (nextExpandedRangeCodes.has(rangeCode)) {
        nextExpandedRangeCodes.delete(rangeCode);
      } else {
        nextExpandedRangeCodes.add(rangeCode);
      }
      return nextExpandedRangeCodes;
    });
  }

  private isRangeExpanded(rangeCode: string): boolean {
    return this.expandedRangeCodes().has(rangeCode);
  }

  private sortGroupItems(
    items: BatterySizingRangeResult[],
  ): BatterySizingRangeResult[] {
    const sortState = this.activeSort();
    const sortedItems = items.slice();

    if (!sortState.column || !sortState.order) {
      if (
        sortedItems.some(
          (item) => item.status === BATTERY_SIZING_STATUS.Selected,
        )
      ) {
        return sortedItems.sort((resultA, resultB) =>
          resultA.status === BATTERY_SIZING_STATUS.Selected
            ? -1
            : resultB.status === BATTERY_SIZING_STATUS.Selected
              ? 1
              : 0,
        );
      }

      return sortedItems;
    }

    const sortColumn = sortState.column;
    const direction = sortState.order === 'ascend' ? 1 : -1;

    return sortedItems.sort((resultA, resultB) => {
      const resultAInfeasible =
        resultA.status === BATTERY_SIZING_STATUS.Infeasible;
      const resultBInfeasible =
        resultB.status === BATTERY_SIZING_STATUS.Infeasible;

      if (resultAInfeasible && !resultBInfeasible) {
        return 1;
      }
      if (!resultAInfeasible && resultBInfeasible) {
        return -1;
      }
      if (resultAInfeasible && resultBInfeasible) {
        return 0;
      }

      const valueA = this.sortValueByColumn(sortColumn, resultA);
      const valueB = this.sortValueByColumn(sortColumn, resultB);

      if (valueA === null && valueB !== null) {
        return 1;
      }
      if (valueA !== null && valueB === null) {
        return -1;
      }
      if (valueA === null && valueB === null) {
        return 0;
      }

      const numericValueA = valueA ?? 0;
      const numericValueB = valueB ?? 0;
      return (numericValueA - numericValueB) * direction;
    });
  }

  private sortValueByColumn(
    column: SortableColumn,
    result: BatterySizingRangeResult,
  ): number | null {
    const config = result.configuration;
    if (!config) {
      return null;
    }

    if (column === 'blockCount') {
      return (config.parallels ?? 0) * (config.seriesBlocks ?? 0);
    }

    if (column === 'capacity') {
      const outputLabel = this.normalizeNumericValue(config.c10OutputLabelAh);
      if (outputLabel !== null) {
        return outputLabel;
      }

      const nominal = this.normalizeNumericValue(config.c10NominalAh);
      return nominal ?? 0;
    }

    if (column === 'backupTime') {
      return this.normalizeNumericValue(config.realBackupMinutes);
    }

    return this.normalizeNumericValue(result.batteryTotalPrice);
  }

  private normalizeNumericValue(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
      return value;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return null;
      }

      const normalized = trimmed
        .replace(/\s+/g, '')
        .replace(',', '.')
        .replace(/[^0-9.-]/g, '');
      if (!normalized) {
        return null;
      }

      const asNumber = Number.parseFloat(normalized);
      if (Number.isFinite(asNumber) && asNumber >= 0) {
        return asNumber;
      }
    }

    return null;
  }
}
